import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium'
import { ValidUuidString } from '@omnea-app/packages/universal/string-utils/isUuid'
import { isIncompleteFilterItem } from '@omnea-app/requests/requestsFrontendClient'
import {
  isMyTasksFilterItemServable,
  isMyTasksSortFieldServable,
  TasksTopLevelFilterKey,
} from '@omnea-app/tasks/tasksFrontendClient'

export type ServerTaskQuery = { filterModel: GridFilterModel; sortModel: GridSortModel }

type TasksViewState = {
  filterModel: GridFilterModel
  sortModel: GridSortModel
  columnFields: ReadonlySet<string>
  topLevelFilterKey: TasksTopLevelFilterKey | ValidUuidString
}

// The grid's view as a query the server can answer exactly, or null when one of its clauses (status, due
// date, assignees…) can only be applied to the full list in the browser. A sort item naming no column (the
// default `dueDate` sort; the column is `dueOn`) is ignored by the grid, so it is dropped here too rather
// than sent; half-built filter items are left for the server, which skips them as the grid does.
// A quick search stays in the browser: the grid searches every column it shows (step label, status…), while the
// server searches request fields only, so a server page would miss tasks matching on the others.
// Pending also lists goods-received and bill-approval tasks and intake amendments, which have no request for a
// filter, search or sort to read, so /paginated leaves them out of anything but the unfiltered view; a pending
// view that filters, searches or sorts loads every task instead, so the browser keeps them listed.
export const toServerTaskQuery = ({
  filterModel,
  sortModel,
  columnFields,
  topLevelFilterKey,
}: TasksViewState): ServerTaskQuery | null => {
  const query = { filterModel, sortModel: sortModel.filter(({ field, sort }) => sort != null && columnFields.has(field)) }
  if (hasQuickSearch(filterModel)) return null
  if (topLevelFilterKey === 'pending') return isPlainServerTaskQuery(query) ? query : null
  const isServable =
    query.sortModel.every(({ field }) => isMyTasksSortFieldServable(field)) &&
    filterModel.items.every((item) => isIncompleteFilterItem(item) || isMyTasksFilterItemServable(item))
  return isServable ? query : null
}

// No clause that filters or orders anything: the existing endpoint's newest-first page answers it, so the
// default view keeps working without /paginated.
export const isPlainServerTaskQuery = ({ filterModel, sortModel }: ServerTaskQuery) =>
  sortModel.length === 0 && filterModel.items.every((item) => isIncompleteFilterItem(item)) && !hasQuickSearch(filterModel)

const hasQuickSearch = ({ quickFilterValues }: GridFilterModel) =>
  (quickFilterValues ?? []).some((term) => String(term).trim() !== '')
